import type { BlockDTO, BlockType, IconData } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { ListView } from 'src/common/components/list-view';
import { ListItemType } from 'src/common/components/list-view/types';
import { LoadingIcon } from 'src/common/components/loading-icon';
import { request } from 'src/common/request';
import { segmentsToText } from 'src/editor/utils/editor';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { blocksActions } from 'src/redux/reducers/blocks';
import { dispatch } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { isPageLike } from 'src/utils/block-type-utils';
import { getUntitledName } from 'src/utils/get-untitled-name';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import type { SearchItem } from '../search';
import { Icon } from 'src/common/components/icon';
import { cx } from '@flowus/common/cx';

interface Props {
  onSelect?(pageInfo: SearchItem): void;
  selectedItems?: Record<string, boolean>;
  className?: string;
  maxRecently?: number;
}
const MAX_RECENTLY = 10;
export const RecentlyList: FC<Props> = ({
  onSelect,
  className,
  selectedItems,
  maxRecently = MAX_RECENTLY,
}) => {
  const { t } = useAppTranslation();
  const [loading, setLoading] = useState(true);
  const [searchItems, setSearchItems] = useState<
    {
      type: ListItemType;
      data: {
        uuid: string;
        title: string;
        icon: IconData | undefined;
        type: BlockType;
        backgroundColor: string | undefined;
      };
    }[]
  >([]);
  const items = searchItems.map((item) => {
    return {
      ...item,
      data: {
        ...item.data,
        renderRight: selectedItems
          ? () => {
              const isSelected = selectedItems[item.data.uuid];
              return <Icon name={isSelected ? 'IcCheckbox2Check' : 'IcCheckbox2'} size="middle" />;
            }
          : undefined,
      },
    };
  });

  useEffect(() => {
    const loadRecentlyHistory = async () => {
      const response = await request.editor.querySpaceVisitsHistory(getCurrentSpaceId(), 0);
      setLoading(false);
      if (typeof response !== 'object') return;

      const { results, recordMap } = response;
      if (!results) return;
      if (!recordMap) return;

      const { blocks } = recordMap;
      if (!blocks) return;

      dispatch(blocksActions.update({ blocks: blocks as Record<string, NextBlock> }));

      const getItem = (uuid?: string) => {
        if (uuid) {
          const block = blocks[uuid];
          if (isPageLike(block?.type)) {
            return block;
          }
        }
      };
      const recent: BlockDTO[] = [];
      let count = 0;
      // 只显示MAX_RECENTLY个数的最近浏览记录
      for (let i = 0; count < maxRecently && i < results.length; i++) {
        const uuid = results[i]?.uuid;
        const item = getItem(uuid);
        if (item && recent.length < maxRecently) {
          count++;
          recent.push(item);
        }
      }
      const items = recent.map((block) => {
        return {
          type: ListItemType.SEARCH,
          data: {
            uuid: block.uuid,
            title: segmentsToText(block.data.segments) || getUntitledName(block.type),
            icon: block.data?.icon,
            type: block.type,
            backgroundColor: block.backgroundColor,
          },
        };
      });
      setSearchItems(items);
    };
    void loadRecentlyHistory();
  }, [selectedItems]);

  const handleMenuItemClick = (_: unknown, index: number) => {
    const searchItem = searchItems[index];
    if (searchItem) {
      // @ts-ignore:  I know what I do
      onSelect?.(searchItem.data);
    }
  };

  return (
    <div className={cx('relative overflow-y-auto h-[300px]', className)}>
      <ListView
        items={items}
        hasMore={false}
        scrollContainerStyle={{ top: '52px' }}
        onItemClick={handleMenuItemClick}
        customFooter={
          <>
            {loading && (
              <div className="relative flex flex-row items-center justify-center h-10">
                <LoadingIcon size="middle" />
                <div className="flex items-center ml-2 text-center text-t2 text-grey4">
                  {t('common.loading')}
                </div>
              </div>
            )}
            {!loading && searchItems.length === 0 && (
              <div className="text-grey4 text-center mt-[100px]">
                {t('moveTo.no_recent_history')}
              </div>
            )}
          </>
        }
      />
    </div>
  );
};
